import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import RealTimeStatusBadge from '../../components/ui/RealTimeStatusBadge';
import MetricsCard from './components/MetricsCard';
import FilterControls from './components/FilterControls';
import LiveOutageFeed from './components/LiveOutageFeed';
import TrendingOutages from './components/TrendingOutages';
import QuickActions from './components/QuickActions';

const OutageDashboard = () => {
  const navigate = useNavigate();
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [filters, setFilters] = useState({
    category: 'all',
    severity: 'all',
    region: 'all',
    timeRange: 'live'
  });

  // Simulate real-time connection status
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
      
      // Occasionally simulate connection issues
      const shouldSimulateIssue = Math.random() > 0.95;
      if (shouldSimulateIssue) {
        setConnectionStatus('connecting');
        setTimeout(() => setConnectionStatus('connected'), 2000);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleViewDetails = (outageId) => {
    navigate(`/service-outage-details?id=${outageId}`);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Mock metrics data
  const metricsData = [
    {
      title: "Active Outages",
      value: "23",
      change: "+5",
      changeType: "negative",
      icon: "AlertTriangle",
      description: "Currently reported incidents"
    },
    {
      title: "Affected Users",
      value: "2.4M",
      change: "+12%",
      changeType: "negative",
      icon: "Users",
      description: "Users experiencing issues"
    },
    {
      title: "Services Monitored",
      value: "1,247",
      change: "+8",
      changeType: "positive",
      icon: "Server",
      description: "Tracked service providers"
    },
    {
      title: "Reports Today",
      value: "8,932",
      change: "+23%",
      changeType: "negative",
      icon: "FileText",
      description: "User-submitted reports"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Main Content */}
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl font-bold text-foreground">Outage Dashboard</h1>
              <p className="text-muted-foreground">
                Real-time monitoring of service outages and incidents
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <RealTimeStatusBadge 
                connectionStatus={connectionStatus}
                lastUpdate={lastUpdate}
              />
              <Button
                onClick={() => navigate('/report-outage')}
                iconName="Plus"
                iconPosition="left"
              >
                Report Outage
              </Button>
            </div>
          </div>

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {metricsData?.map((metric, index) => (
              <MetricsCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                description={metric?.description}
              />
            ))}
          </div>

          {/* Filter Controls */}
          <div className="mb-6">
            <FilterControls
              onFiltersChange={handleFiltersChange}
              activeFilters={filters}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Live Feed */}
            <div className="lg:col-span-2 space-y-6">
              {/* Live Outage Feed Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Icon name="Activity" size={20} className="text-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">Live Outage Feed</h2>
                  <div className="status-indicator status-online"></div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={14} />
                  <span>Updated {lastUpdate?.toLocaleTimeString()}</span>
                </div>
              </div>

              {/* Live Feed */}
              <LiveOutageFeed
                filters={filters}
                onViewDetails={handleViewDetails}
              />
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <QuickActions />

              {/* Trending Outages */}
              <TrendingOutages onViewDetails={handleViewDetails} />

              {/* Geographic Hotspots */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="MapPin" size={20} className="text-foreground" />
                  <h3 className="font-semibold text-foreground">Geographic Hotspots</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { region: "California", outages: 8, severity: "high" },
                    { region: "Texas", outages: 5, severity: "medium" },
                    { region: "New York", outages: 4, severity: "medium" },
                    { region: "Florida", outages: 3, severity: "low" }
                  ]?.map((hotspot, index) => (
                    <div key={index} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${
                          hotspot?.severity === 'high' ? 'bg-error' :
                          hotspot?.severity === 'medium' ? 'bg-warning' : 'bg-success'
                        }`}></div>
                        <span className="text-sm font-medium text-foreground">{hotspot?.region}</span>
                      </div>
                      <span className="text-sm text-muted-foreground">{hotspot?.outages} outages</span>
                    </div>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/geographic-outage-map')}
                  className="w-full mt-3"
                >
                  View Full Map
                  <Icon name="ExternalLink" size={14} className="ml-2" />
                </Button>
              </div>

              {/* Recent Activity */}
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="Clock" size={20} className="text-foreground" />
                  <h3 className="font-semibold text-foreground">Recent Activity</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { action: "New outage reported", service: "Discord", time: "2m ago" },
                    { action: "Outage resolved", service: "Spotify", time: "15m ago" },
                    { action: "Severity updated", service: "Netflix", time: "23m ago" },
                    { action: "New outage reported", service: "Steam", time: "1h ago" }
                  ]?.map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3 text-sm">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1">
                        <p className="text-foreground">{activity?.action}</p>
                        <p className="text-muted-foreground">{activity?.service} • {activity?.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OutageDashboard;